import React, { useContext } from 'react';
import styled from 'styled-components';

import { ApplicationUIContext, ContentWrapper } from '@yuhuinc/ui-library';
import { Form, Input, Checkbox, Card, Tabs, Typography, Space, Divider } from 'antd';
import { TextEditor } from '@yuhuinc/ui-library';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getTranslatedString } from 'helpers/i18n';

const { Title, Paragraph } = Typography;

const StyledSpace = styled(Space)`
  width: 100%;
`;

const requiredField = {
  required: true,
  message: getTranslatedString('requiredField'),
};

const locales = {
  en: { key: 'en', label: getTranslatedString('languages.en') },
  fr: { key: 'fr', label: getTranslatedString('languages.fr') },
};

export default function TemplateForm({ form, formName, formData = {}, children }) {
  const { isMobile } = useContext(ApplicationUIContext);
  const selectedLanguages = Form.useWatch('selected_languages', form);

  const CustomTextEditorInput = ({ value, onChange, editorId }) => (
    <TextEditor defaultValue={value} editorId={editorId} onChange={onChange} />
  );

  const iselectedLanguage = (locale) => selectedLanguages?.includes(locale);

  const FormFields = ({ locale }) => (
    <>
      <StyledSpace direction="vertical" size="middle">
        <Form.Item
          name={['translations', locale, 'name']}
          label={getTranslatedString('landlordPortal.applicationTemplate.form.name')}
          rules={[requiredField]}
          hasFeedback
        >
          <Input />
        </Form.Item>

        <Form.Item
          name={['translations', locale, 'description']}
          label={getTranslatedString('landlordPortal.applicationTemplate.form.description')}
          requiredMark={'optional'}
          tooltip={{ title: '', icon: <InfoCircleOutlined /> }}
        >
          <Input.TextArea rows={2} />
        </Form.Item>
      </StyledSpace>

      <Divider />

      <Title level={5}>
        {getTranslatedString('landlordPortal.applicationTemplate.form.terms')}
      </Title>

      <Paragraph>
        {getTranslatedString('landlordPortal.applicationTemplate.terms.description')}
      </Paragraph>

      <Form.Item
        name={['translations', locale, 'terms_and_conditions']}
        label={getTranslatedString('landlordPortal.applicationTemplate.form.terms')}
        rules={[requiredField]}
      >
        <CustomTextEditorInput editorId={`${locale}_terms_and_conditions`} />
      </Form.Item>

      <Form.Item
        name={['translations', locale, 'consent_text']}
        label={getTranslatedString('landlordPortal.applicationTemplate.form.consentText')}
        rules={[requiredField]}
        hasFeedback
      >
        <Input />
      </Form.Item>

      <Divider />

      <Title level={5}>
        {getTranslatedString('landlordPortal.applicationTemplate.applicationOverview.title')}
      </Title>

      <Paragraph>
        {getTranslatedString('landlordPortal.applicationTemplate.applicationOverview.description')}
      </Paragraph>

      <Form.Item
        name={['translations', locale, 'application_overview_title']}
        label={getTranslatedString(
          'landlordPortal.applicationTemplate.form.applicationOverviewTitle'
        )}
        rules={[requiredField]}
        hasFeedback
      >
        <Input />
      </Form.Item>

      <Form.Item
        name={['translations', locale, 'application_overview_text']}
        label={getTranslatedString(
          'landlordPortal.applicationTemplate.form.applicationOverviewText'
        )}
        rules={[requiredField]}
      >
        <CustomTextEditorInput editorId={`${locale}_application_overview_text`} />
      </Form.Item>

      <Divider />

      <Title level={5}>
        {getTranslatedString('landlordPortal.applicationTemplate.depositPayment.title')}
      </Title>
      <Paragraph>
        {getTranslatedString('landlordPortal.applicationTemplate.depositPayment.description')}
      </Paragraph>

      <Form.Item
        name={['translations', locale, 'deposit_payment_title']}
        label={getTranslatedString('landlordPortal.applicationTemplate.form.depositPaymentTitle')}
        rules={[requiredField]}
        hasFeedback
      >
        <Input />
      </Form.Item>

      <Form.Item
        name={['translations', locale, 'deposit_payment_description']}
        label={getTranslatedString(
          'landlordPortal.applicationTemplate.form.depositPaymentDescription'
        )}
        rules={[requiredField]}
      >
        <CustomTextEditorInput editorId={`${locale}_deposit_payment_description`} />
      </Form.Item>

      <Divider />

      <Title level={5}>
        {getTranslatedString('landlordPortal.applicationTemplate.confirmDetails.title')}
      </Title>
      <Paragraph>
        {getTranslatedString('landlordPortal.applicationTemplate.confirmDetails.description')}
      </Paragraph>

      <Form.Item
        name={['translations', locale, 'confirmation_detail_title']}
        label={getTranslatedString(
          'landlordPortal.applicationTemplate.form.confirmationDetailTitle'
        )}
        rules={[requiredField]}
        hasFeedback
      >
        <Input />
      </Form.Item>

      <Form.Item
        name={['translations', locale, 'confirmation_detail_description']}
        label={getTranslatedString(
          'landlordPortal.applicationTemplate.form.confirmationDetailDescription'
        )}
        rules={[requiredField]}
        hasFeedback
      >
        <Input.TextArea />
      </Form.Item>

      <Form.Item name={['translations', locale, 'locale']} initialValue={locale} hidden>
        <Input />
      </Form.Item>
    </>
  );

  const formInitialValues = {
    status: formData?.status || 'draft',
    selected_languages: formData?.selected_languages || ['en'],
    translations: formData?.translations || {},
    ...formData,
  };

  return (
    <ContentWrapper>
      <Card style={{ maxWidth: 566 }} bodyStyle={{ padding: isMobile && 16 }}>
        <Title level={4}>
          {getTranslatedString('landlordPortal.applicationTemplate.subtitle')}
        </Title>

        <Divider />

        <Form
          form={form}
          layout="vertical"
          size={isMobile && 'large'}
          formname={formName}
          initialValues={formInitialValues}
          scrollToFirstError
        >
          <Form.Item
            name={'selected_languages'}
            label={getTranslatedString('landlordPortal.applicationTemplate.form.languages')}
            rules={[requiredField]}
            hasFeedback
          >
            <Checkbox.Group>
              <Checkbox value={locales.en.key} disabled>
                {locales.en.label}
              </Checkbox>
              <Checkbox value={locales.fr.key}>{locales.fr.label}</Checkbox>
            </Checkbox.Group>
          </Form.Item>

          <Tabs defaultActiveKey={locales.en.key}>
            <Tabs.TabPane
              tab={locales.en.label}
              key={locales.en.key}
              disabled={!iselectedLanguage(locales.en.key)}
            >
              <FormFields locale={locales.en.key} />
            </Tabs.TabPane>

            <Tabs.TabPane
              tab={locales.fr.label}
              key={locales.fr.key}
              disabled={!iselectedLanguage(locales.fr.key)}
            >
              <FormFields locale={locales.fr.key} />
            </Tabs.TabPane>
          </Tabs>

          <Divider />

          {children}
        </Form>
      </Card>
    </ContentWrapper>
  );
}
